import { useEffect, useMemo } from 'react';
import ClassFormModal from '@/features/class/manage/ClassFormModal';
import { registeredBoardToManagedClass } from '@/features/class/manage/classMappers';
import { useClassManageStore } from '@/features/class/manage/classManageStore';
import { getBoardByKey, type RegisteredBoard } from '@/features/class/classRegistry';
import { useAuthStore } from '@/store/authStore';
import type { ManagedClass } from '@/features/class/manage/types';

function pickString(source: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function pickBoolean(
  source: Record<string, unknown>,
  keys: readonly string[],
): boolean | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return value !== 0;
  }
  return undefined;
}

function normalizeBoardId(value: unknown): string | undefined {
  const id = typeof value === 'number' || typeof value === 'string' ? String(value).trim() : '';
  return /^\d+$/.test(id) ? id : undefined;
}

function rawUserBoards(rawUser: unknown): Record<string, unknown>[] {
  if (!rawUser || typeof rawUser !== 'object') return [];
  const root = rawUser as Record<string, unknown>;
  const rows: Record<string, unknown>[] = [];

  const addBoard = (value: unknown) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      rows.push(value as Record<string, unknown>);
    }
  };

  const categorized = root.boardWithCategory;
  if (Array.isArray(categorized)) {
    for (const group of categorized) {
      if (!group || typeof group !== 'object') continue;
      const boards = (group as Record<string, unknown>).boards;
      if (Array.isArray(boards)) boards.forEach(addBoard);
    }
  }

  const flat = root.board;
  if (Array.isArray(flat)) flat.forEach(addBoard);

  return rows;
}

function findBoardInRawUser(rawUser: unknown, key: string): RegisteredBoard | undefined {
  const normalizedKey = key.trim();
  if (!normalizedKey) return undefined;
  const upperKey = normalizedKey.toUpperCase();

  for (const board of rawUserBoards(rawUser)) {
    const id = normalizeBoardId(pickString(board, ['id', 'boardId']));
    if (!id) continue;
    const code = pickString(board, ['code']) ?? id;
    const name = pickString(board, ['name', 'title']) ?? code;
    if (
      id === normalizedKey ||
      code.toUpperCase() === upperKey ||
      name.toUpperCase() === upperKey
    ) {
      return {
        id,
        code,
        name,
        isPrivate: pickBoolean(board, ['isPrivate']),
      };
    }
  }

  return undefined;
}

/** Renders the create/edit class modal when the global store opens it. */
export default function ClassFormModalHost() {
  const modalMode = useClassManageStore((state) => state.modalMode);
  const editingClassId = useClassManageStore((state) => state.editingClassId);
  const closeClassModal = useClassManageStore((state) => state.closeClassModal);
  const rawUser = useAuthStore((state) => state.rawUser);
  const storeClass = useClassManageStore((state) =>
    editingClassId ? state.classes.find((item) => item.id === editingClassId) : undefined,
  );
  const registryClass = useMemo(
    () => (editingClassId ? getBoardByKey(editingClassId) : undefined),
    [editingClassId],
  );
  const rawUserClass = useMemo(
    () => (editingClassId ? findBoardInRawUser(rawUser, editingClassId) : undefined),
    [editingClassId, rawUser],
  );
  const editBoardId = useMemo(() => {
    return (
      normalizeBoardId(editingClassId) ??
      normalizeBoardId(registryClass?.id) ??
      normalizeBoardId(rawUserClass?.id)
    );
  }, [editingClassId, rawUserClass?.id, registryClass?.id]);

  // Resolve the class being edited with ALL of its values. Seeded/managed
  // classes carry the full form data (type, year, subject, grade, credits,
  // privacy). Real API boards opened from the profile/sidebar aren't in the
  // manage store, so fall back to the runtime board registry and synthesize a
  // ManagedClass — otherwise the modal would silently fail to open for them.
  const editingClass = useMemo<ManagedClass | null>(() => {
    if (!editingClassId) {
      return null;
    }
    if (storeClass) {
      return storeClass;
    }
    const board = registryClass ?? rawUserClass;
    if (board) {
      return registeredBoardToManagedClass(board);
    }
    return editBoardId
      ? registeredBoardToManagedClass({
          id: editBoardId,
          code: editBoardId,
          name: editBoardId,
        })
      : null;
  }, [editBoardId, editingClassId, rawUserClass, registryClass, storeClass]);

  // Never leave the store wedged in edit mode when the target can't be
  // resolved at all (e.g. a stale id lingering after the class was deleted).
  // Auto-close so a later create/edit is not blocked by a stuck state.
  useEffect(() => {
    if (modalMode === 'edit' && editingClassId && !editingClass) {
      closeClassModal();
    }
  }, [modalMode, editingClassId, editingClass, closeClassModal]);

  if (!modalMode) {
    return null;
  }

  if (modalMode === 'edit' && !editingClass) {
    return null;
  }

  return (
    <ClassFormModal
      mode={modalMode}
      editingClass={editingClass}
      boardId={editBoardId}
      onClose={closeClassModal}
    />
  );
}
